'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";

function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }

function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }

import Immutable from 'immutable';
import mapKeys from 'hs-lodash/mapKeys';
import { parse as parseQuery, stringify as stringifyQuery } from 'hub-http/helpers/params';
import * as DataSync from 'ecosystem-marketplace-core/constants/dataSyncs/Sync';
import * as Tier from 'ecosystem-marketplace-core/constants/groupTags/Tier';
import * as HubSpotFeature from 'ecosystem-marketplace-core/constants/groupTags/HubSpotFeature';
import { BY_CATEGORY as FEATURES_BY_CATEGORY } from 'ecosystem-marketplace-core/constants/groupTags/categoryFeatures';
import * as TierGroup from 'ecosystem-marketplace-core/constants/groupTags/TierGroup';
import * as PlanType from 'ecosystem-marketplace-core/constants/pricings/PlanType';
import * as FilterKey from '../constants/search/FilterKey';
import * as SortField from '../constants/search/SortField';
import * as SortOrder from '../constants/search/SortOrder';
import * as SupportSource from 'ecosystem-marketplace-core/constants/search/SupportSource';
import { filterFeaturesByCategory } from '../utils/groupTags';
import { getCategoryFilterUrl, getSuperCategoryFilterUrl, getAllCategoriesUrl } from '../utils/urls';
import { APP_LANGUAGES } from '../constants/search/FilterKey';
const EMPTY_SET = Immutable.Set();
const {
  CATEGORY,
  CERTIFIED,
  SYNC,
  CATEGORY_FEATURES,
  INTEGRATIONS,
  PLAN_COMPATIBLE,
  PRICING,
  SUPER_CATEGORY,
  SUPPORT_SOURCE,
  SUPPORT_LANGUAGES,
  TIERS,
  HUBSPOT_FEATURES,
  OPS_HUB,
  SEARCH,
  SORTS,
  SORT_FIELD,
  SORT_ORDER,
  SHOW_PREFERRED_LANG_FIRST
} = FilterKey;
const FILTER_KEY_VALUES = Immutable.List(Object.values(FilterKey));
const CATEGORY_FEATURES_SET = Object.values(FEATURES_BY_CATEGORY).reduce((final, current) => final.union(Object.values(current)), EMPTY_SET);
const TIERS_SET = Immutable.Set(Object.values(Tier));
const HUBSPOT_FEATURES_SET = Immutable.Set(Object.values(HubSpotFeature));
const DATA_SYNCS_SET = Immutable.Set(Object.values(DataSync));
const SUPPORT_SOURCE_SET = Immutable.Set(Object.values(SupportSource));
const PLAN_TYPE_SET = Immutable.Set(Object.values(PlanType));
const SORT_ORDER_SET = Immutable.Set(Object.values(SortOrder));
const SORT_FIELD_SET = Immutable.Set(Object.values(SortField));

const isOpsHubTier = tier => TierGroup.OPERATIONS.includes(tier);

const filterKeyOrder = Immutable.OrderedSet([CATEGORY_FEATURES, PRICING, PLAN_COMPATIBLE, TIERS, HUBSPOT_FEATURES, INTEGRATIONS, CERTIFIED, SYNC, APP_LANGUAGES, SUPPORT_LANGUAGES, SUPPORT_SOURCE, SEARCH, SORT_FIELD, SORT_ORDER, SHOW_PREFERRED_LANG_FIRST]);
const filterValuesSetMap = Immutable.Map({
  [CATEGORY_FEATURES]: CATEGORY_FEATURES_SET,
  [TIERS]: TIERS_SET,
  [HUBSPOT_FEATURES]: HUBSPOT_FEATURES_SET,
  [SYNC]: DATA_SYNCS_SET,
  [SUPPORT_SOURCE]: SUPPORT_SOURCE_SET,
  [PRICING]: PLAN_TYPE_SET,
  [SORT_FIELD]: SORT_FIELD_SET,
  [SORT_ORDER]: SORT_ORDER_SET
});
const filterKeyEventPropertyMap = Immutable.Map({
  [SUPER_CATEGORY]: 'category',
  [CATEGORY]: 'subcategory',
  [CATEGORY_FEATURES]: 'feature',
  [PRICING]: 'paymentType',
  [PLAN_COMPATIBLE]: 'isCompatible',
  [TIERS]: 'tier',
  [HUBSPOT_FEATURES]: 'tool',
  [INTEGRATIONS]: 'integration',
  [CERTIFIED]: 'listingStatus',
  [SYNC]: 'integrationsSyncAccess',
  [APP_LANGUAGES]: 'appLanguages',
  [SUPPORT_LANGUAGES]: 'language',
  [SUPPORT_SOURCE]: 'provider',
  [SORT_FIELD]: 'sortField',
  [SORT_ORDER]: 'sortOrder',
  [SEARCH]: 'search',
  [SHOW_PREFERRED_LANG_FIRST]: 'showPreferredLanguageFirst'
});
const PARAM_PREFIX = 'eco_';
const defaults = {
  [CATEGORY]: null,
  [SUPER_CATEGORY]: null,
  [CATEGORY_FEATURES]: EMPTY_SET,
  [PRICING]: null,
  [PLAN_COMPATIBLE]: false,
  [TIERS]: EMPTY_SET,
  [HUBSPOT_FEATURES]: EMPTY_SET,
  [INTEGRATIONS]: EMPTY_SET,
  [CERTIFIED]: false,
  [SYNC]: null,
  [APP_LANGUAGES]: EMPTY_SET,
  [SUPPORT_LANGUAGES]: EMPTY_SET,
  [SUPPORT_SOURCE]: null,
  [SEARCH]: '',
  [SORT_FIELD]: null,
  [SORT_ORDER]: null,
  [SHOW_PREFERRED_LANG_FIRST]: false
};

class StorefrontFilters extends Immutable.Record(defaults) {
  static from(filters) {
    const cleanedFilters = Object.assign({}, filters, {
      [CATEGORY_FEATURES]: Immutable.Set(filters[CATEGORY_FEATURES]),
      [TIERS]: Immutable.Set(filters[TIERS]),
      [HUBSPOT_FEATURES]: Immutable.Set(filters[HUBSPOT_FEATURES]),
      [INTEGRATIONS]: Immutable.Set(filters[INTEGRATIONS]),
      [SUPPORT_LANGUAGES]: Immutable.Set(filters[SUPPORT_LANGUAGES])
    });
    return new StorefrontFilters(cleanedFilters);
  }

  fromQueryString(queryString) {
    const {
      [CATEGORY]: category
    } = this;

    const queryDefaults = _objectWithoutPropertiesLoose(defaults, [CATEGORY, SUPER_CATEGORY].map(_toPropertyKey));

    const parsedQuery = mapKeys(parseQuery(queryString), (__value, key) => key.replace(PARAM_PREFIX, ''));
    Object.keys(parsedQuery).forEach(key => {
      if (!FILTER_KEY_VALUES.includes(key)) {
        delete parsedQuery[key];
        return;
      }

      const defaultValue = defaults[key];
      const isSet = Immutable.Set.isSet(defaultValue);
      let value = parsedQuery[key];

      if (Array.isArray(value) && !isSet) {
        value = value[0];
      }

      if (isSet) {
        value = Immutable.Set(!Array.isArray(value) ? [value] : value);
      }

      if (key === CERTIFIED || key === PLAN_COMPATIBLE) {
        value = value === 'true';
      }

      if (key === CATEGORY_FEATURES && category) {
        value = filterFeaturesByCategory(value, category);
      } else if (filterValuesSetMap.has(key)) {
        const valuesSet = filterValuesSetMap.get(key);

        if (Immutable.Set.isSet(value)) {
          value = value.filter(val => valuesSet.includes(val));
        } else if (!valuesSet.includes(value)) {
          delete parsedQuery[key];
          return;
        }
      }

      parsedQuery[key] = value;
    });
    return this.merge(Object.assign({}, queryDefaults, {}, parsedQuery)).clean();
  }

  toQuery() {
    const query = this.clean().toJS();
    Object.keys(query).forEach(key => {
      const value = query[key];
      const isArray = Array.isArray(value);

      if (!value || isArray && !value.length) {
        delete query[key];
        return;
      }

      if (isArray) {
        query[key] = value.sort();
      }
    });
    return query;
  }

  toQueryString() {
    const query = this.toQuery();
    delete query[SUPER_CATEGORY];
    delete query[CATEGORY];
    return filterKeyOrder.reduce((final, key) => {
      const value = query[key];

      if (value != null && value !== defaults[key]) {
        final.push(stringifyQuery({
          [`${PARAM_PREFIX}${key}`]: value
        }));
      }

      return final;
    }, []).join('&');
  }

  toSearchQuery(extra = {}) {
    const query = this.toQuery();
    const {
      [PRICING]: planType,
      [SEARCH]: search,
      [SORT_FIELD]: sortField,
      [SORT_ORDER]: sortOrder = SortOrder.DESC,
      [TIERS]: tiers
    } = query;

    if (sortField) {
      query[SORTS] = [{
        sortField,
        sortOrder
      }];
      delete query[SORT_FIELD];
      delete query[SORT_ORDER];
    }

    if (sortField === SortField.PRICE) {
      let priceSortField;

      switch (planType) {
        case PlanType.MONTHLY:
          {
            priceSortField = SortField.MONTHLY_PRICE;
            break;
          }

        case PlanType.YEARLY:
          {
            priceSortField = SortField.YEARLY_PRICE;
            break;
          }

        case PlanType.ONE_TIME:
          {
            priceSortField = SortField.ONE_TIME_PRICE;
            break;
          }

        default:
          break;
      }

      query[SORTS] = [{
        sortField: priceSortField,
        sortOrder
      }];
    }

    if (!sortField && !search) {
      query[SORTS] = [{
        sortField: SortField.HUB_INSTALLS,
        sortOrder: SortOrder.DESC
      }];
    }

    const hasOpsHubTier = tiers && tiers.some(isOpsHubTier);

    if (hasOpsHubTier) {
      query[OPS_HUB] = true;
    }

    if (!query[TIERS] || query[TIERS].length < 1) {
      delete query[TIERS];
    }

    delete query[SHOW_PREFERRED_LANG_FIRST]; // This property isn't intended to be included in the search query sent to the API.

    return Object.assign({}, query, {}, extra);
  }

  toEventProperties() {
    const query = this.toQuery();
    const {
      [CERTIFIED]: certified,
      [SEARCH]: search
    } = query;
    const properties = {};
    Object.keys(query).forEach(key => {
      const propertyKey = filterKeyEventPropertyMap.get(key);

      if (propertyKey) {
        properties[propertyKey] = query[key];
      }
    });

    if (certified) {
      properties.listingStatus = 'CERTIFIED';
    }

    if (search) {
      properties.searchLength = search.length;
    }

    return properties;
  }

  toLocation(collectionSlug) {
    const {
      [SUPER_CATEGORY]: superCategory,
      [CATEGORY]: category
    } = this;
    let pathname;

    if (category) {
      pathname = getCategoryFilterUrl(category, superCategory);
    } else if (superCategory) {
      pathname = getSuperCategoryFilterUrl(superCategory);
    } else {
      pathname = collectionSlug ? `/${collectionSlug}` : getAllCategoriesUrl();
    }

    return {
      pathname,
      search: `?${this.toQueryString()}`
    };
  }

  toAllCategories() {
    return this.withMutations(mutableFilters => {
      mutableFilters.delete(SUPER_CATEGORY).delete(CATEGORY);
    });
  }

  clear(nuke = false) {
    if (nuke) {
      return super.clear();
    }

    return this.withMutations(mutableFilters => {
      mutableFilters.delete(CATEGORY_FEATURES).delete(PRICING).delete(PLAN_COMPATIBLE).delete(TIERS).delete(HUBSPOT_FEATURES).delete(INTEGRATIONS).delete(CERTIFIED).delete(SYNC).delete(APP_LANGUAGES).delete(SUPPORT_LANGUAGES).delete(SUPPORT_SOURCE);
    });
  }

  clean() {
    const {
      [PRICING]: planType,
      [PLAN_COMPATIBLE]: compatible,
      [TIERS]: tiers,
      [SEARCH]: search,
      [SORT_FIELD]: sortField
    } = this;
    return this.withMutations(mutableFilters => {
      if (sortField) {
        if (sortField === SortField.RELEVANCE && !search) {
          mutableFilters.delete(SORT_FIELD).delete(SORT_ORDER);
        }

        if (sortField === SortField.RANDOM) {
          mutableFilters.delete(SORT_ORDER);
        }

        if (sortField === SortField.PRICE && planType === PlanType.FREE) {
          mutableFilters.delete(SORT_FIELD).delete(SORT_ORDER);
        }
      }

      if (compatible && tiers) {
        mutableFilters.delete(TIERS);
      }
    });
  }

  getValidFilterTagKeys() {
    return filterKeyOrder.filter(key => {
      const value = this.get(key);

      if (key === SORT_FIELD || key === SORT_ORDER || key === SEARCH || key === SHOW_PREFERRED_LANG_FIRST) {
        return false;
      }

      if (value == null || value === false) {
        return false;
      }

      if (Immutable.Set.isSet(value)) {
        return !value.isEmpty();
      }

      return true;
    });
  }

}

export { filterKeyOrder };
export default StorefrontFilters;